import { DetailItem } from '@/components/DetailItem';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
    checkNumbers,
    foldIdentifier,
    identifierStrength,
    locateIdentifier,
    modelRuleIdentifiers,
    noticeBlocks,
    type NoticeRange,
    type NoticeSectionKey,
    noticeSections,
    serialEnds,
    tokenizeNotice,
} from '@rock/recalls';
import type { MergedRecallRules, RecallRuleSource } from '@rock/types';
import { type FC, useMemo } from 'react';
import { RuleFacts, type RuleFactsProps } from './recallTags';

type Tone = 'verified' | 'unverified' | 'check' | 'harvested' | 'suppressed';

const TONE_CLASS: Record<Tone, string> = {
    verified: 'bg-emerald-100 text-emerald-900 rounded px-0.5',
    unverified: 'bg-amber-100 text-amber-900 rounded px-0.5',
    check: 'bg-sky-100 text-sky-900 rounded px-0.5',
    harvested: 'bg-slate-200 text-slate-800 rounded px-0.5 underline decoration-dotted',
    suppressed: 'bg-slate-200 text-slate-500 rounded px-0.5 line-through decoration-slate-400',
};

const TONE_LEGEND: { tone: Tone; label: string; meaning: string }[] = [
    { tone: 'verified', label: 'verified', meaning: 'A rule, printed in the notice' },
    {
        tone: 'unverified',
        label: 'unverified',
        meaning: 'A rule not printed word for word; matches only once accepted',
    },
    { tone: 'check', label: 'homeowner check', meaning: 'Named in a check the homeowner makes on their unit' },
    { tone: 'harvested', label: 'not in the rules', meaning: 'Looks like an identifier, but unused' },
    { tone: 'suppressed', label: 'removed', meaning: 'Taken out by an admin' },
];

const SECTION_LABELS: Record<NoticeSectionKey, string> = {
    TITLE: 'Title',
    DATE: 'Date',
    PRODUCTS: 'Products',
    MANUFACTURERS: 'Manufacturers',
    DESCRIPTION: 'Description',
    'PAGE TABLES': 'Tables from the recall page',
};

/**
 * The notice exactly as the LLMs read it, one labelled field at a time, with every identifier highlighted
 * in place. The same tokenizer the guard uses finds the spans, so what is lit here is what matched; the
 * fields are ranges of that one text.
 */
export const NoticeFields: FC<{
    noticeText: string;
    rules: MergedRecallRules | undefined;
    harvested: string[];
    suppressed?: string[];
}> = ({ noticeText, rules, harvested, suppressed = [] }) => {
    const segments = useMemo(
        () => highlight(noticeText, rules, harvested, suppressed),
        [noticeText, rules, harvested, suppressed],
    );
    const sections = useMemo(() => noticeSections(noticeText), [noticeText]);
    if (sections.length === 0) {
        return <Highlighted segments={segments} range={{ start: 0, end: noticeText.length }} />;
    }
    return (
        <div className="flex flex-col gap-2.5">
            {sections.map((section) => (
                <DetailItem
                    key={section.key}
                    label={SECTION_LABELS[section.key]}
                    className="space-y-0"
                    valueClassName="font-normal"
                >
                    {section.items?.length === 0 ? (
                        '—'
                    ) : section.items ? (
                        <ul className="m-0 list-disc space-y-0.5 pl-5 text-sm">
                            {section.items.map((item) => (
                                <li key={item.start} className="m-0">
                                    <Highlighted segments={segments} range={item} />
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <NoticeBlocksView noticeText={noticeText} segments={segments} range={section} />
                    )}
                </DetailItem>
            ))}
        </div>
    );
};

export const NoticeLegend: FC = () => (
    <ul className="m-0 flex list-none flex-wrap justify-end gap-x-2 gap-y-1 p-0 text-[11px] italic">
        {TONE_LEGEND.map(({ tone, label, meaning }) => (
            <li key={tone} className="m-0">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <mark className={cn('cursor-help', TONE_CLASS[tone])}>{label}</mark>
                    </TooltipTrigger>
                    <TooltipContent className="not-italic">{meaning}</TooltipContent>
                </Tooltip>
            </li>
        ))}
    </ul>
);

/** A section's prose and markdown tables, each table cell highlighted over its own slice of the notice. */
const NoticeBlocksView: FC<{ noticeText: string; segments: Segment[]; range: NoticeRange }> = ({
    noticeText,
    segments,
    range,
}) => (
    <div className="flex flex-col gap-2">
        {noticeBlocks(noticeText, range).map((block) =>
            block.kind === 'text' ? (
                <Highlighted key={block.start} segments={segments} range={block} />
            ) : (
                <NoticeTableView key={block.start} segments={segments} rows={block.rows} />
            ),
        )}
    </div>
);

const NoticeTableView: FC<{ segments: Segment[]; rows: NoticeRange[][] }> = ({
    segments,
    rows: [header = [], ...body],
}) => (
    <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
            <thead>
                <tr>
                    {header.map((cell) => (
                        <th
                            key={cell.start}
                            className="border border-border bg-muted px-2 py-1 align-top font-semibold"
                        >
                            <Highlighted segments={segments} range={cell} />
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {body.map((row) => (
                    <tr key={row[0]?.start}>
                        {row.map((cell) => (
                            <td key={cell.start} className="border border-border px-2 py-1 align-top">
                                <Highlighted segments={segments} range={cell} />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const TONE_MEANING = new Map(TONE_LEGEND.map(({ tone, meaning }) => [tone, meaning]));

const Highlighted: FC<{ segments: Segment[]; range: NoticeRange }> = ({ segments, range }) => (
    <div className="whitespace-pre-wrap break-words text-sm leading-6">
        {clip(segments, range).map((segment) =>
            segment.tone ? (
                <Tooltip key={segment.start}>
                    <TooltipTrigger asChild>
                        <mark className={TONE_CLASS[segment.tone]}>{segment.text}</mark>
                    </TooltipTrigger>
                    <TooltipContent className="text-pretty max-w-xs">
                        {segment.rule ? <RuleFacts {...segment.rule} /> : TONE_MEANING.get(segment.tone)}
                    </TooltipContent>
                </Tooltip>
            ) : (
                <span key={segment.start}>{segment.text}</span>
            ),
        )}
    </div>
);

interface Segment {
    start: number;
    text: string;
    tone?: Tone;
    /** The rule a verified or unverified highlight belongs to. */
    rule?: RuleFactsProps;
}

function highlight(
    noticeText: string,
    rules: MergedRecallRules | undefined,
    harvested: string[],
    suppressed: string[],
): Segment[] {
    const tokens = tokenizeNotice(noticeText);
    const spans: { start: number; end: number; tone: Tone; rule?: RuleFactsProps }[] = [];
    const add = (folded: string, tone: Tone, rule?: RuleFactsProps) => {
        for (const span of locateIdentifier(tokens, folded)) spans.push({ ...span, tone, rule });
    };
    for (const rule of rules?.modelRules ?? []) {
        const facts = { ...ruleFacts(rule), strength: identifierStrength(rule) };
        for (const id of modelRuleIdentifiers(rule)) add(id, rule.verified ? 'verified' : 'unverified', facts);
    }
    for (const rule of rules?.serialRules ?? []) {
        for (const end of serialEnds(rule.value)) add(end, rule.verified ? 'verified' : 'unverified', ruleFacts(rule));
    }
    // A check's numbers are read by the homeowner, not matched; they get their own colour, not a claim of verification.
    for (const check of rules?.checks ?? []) {
        for (const folded of checkNumbers(check)) add(folded, 'check');
    }
    for (const token of harvested) add(foldIdentifier(token), 'harvested');
    for (const token of suppressed) add(token, 'suppressed');

    // Earlier, longer spans win; a token inside a verified run is not re-marked as harvested.
    spans.sort((a, b) => a.start - b.start || b.end - a.end);
    const segments: Segment[] = [];
    let cursor = 0;
    for (const span of spans) {
        if (span.start < cursor) continue;
        if (span.start > cursor) segments.push({ start: cursor, text: noticeText.slice(cursor, span.start) });
        segments.push({
            start: span.start,
            text: noticeText.slice(span.start, span.end),
            tone: span.tone,
            ...(span.rule ? { rule: span.rule } : {}),
        });
        cursor = span.end;
    }
    if (cursor < noticeText.length) segments.push({ start: cursor, text: noticeText.slice(cursor) });
    return segments;
}

function ruleFacts(rule: {
    source: RecallRuleSource;
    verified: boolean;
    accepted?: boolean;
    note: string | null;
}): RuleFactsProps {
    return { source: rule.source, verified: rule.verified, accepted: rule.accepted, note: rule.note };
}

/** The parts of `segments` inside `range`, cut at its edges. */
function clip(segments: Segment[], range: NoticeRange): Segment[] {
    const clipped: Segment[] = [];
    for (const segment of segments) {
        const start = Math.max(segment.start, range.start);
        const end = Math.min(segment.start + segment.text.length, range.end);
        if (start >= end) continue;
        clipped.push({ ...segment, start, text: segment.text.slice(start - segment.start, end - segment.start) });
    }
    return clipped;
}
